import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('veonfox_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh or login/register endpoints
      if (originalRequest.url?.includes('/auth/refresh') ||
          originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/register')) {
        localStorage.removeItem('veonfox_token');
        localStorage.removeItem('veonfox_refresh');
        localStorage.removeItem('veonfox_auth');
        window.location.href = '/';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('veonfox_refresh');
        const { data } = await api.post('/auth/refresh', { refreshToken });
        const newToken = data.token;
        if (data.refreshToken) localStorage.setItem('veonfox_refresh', data.refreshToken);
        localStorage.setItem('veonfox_token', newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('veonfox_token');
        localStorage.removeItem('veonfox_refresh');
        localStorage.removeItem('veonfox_auth');
        window.location.href = '/';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Response retry interceptor for network issues and 5xx errors (GET requests only)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config } = error;
    
    // Retry only GET requests to prevent side effects on POST/PUT/DELETE
    if (!config || config.method?.toLowerCase() !== 'get') {
      return Promise.reject(error);
    }

    // Identify network drops (no response received) or temporary server 5xx errors
    const isNetworkError = !error.response;
    const is5xxError = error.response && error.response.status >= 500 && error.response.status < 600;

    if (!isNetworkError && !is5xxError) {
      return Promise.reject(error);
    }

    // Configure retry parameters (default to 3 retries)
    config.__retryCount = config.__retryCount || 0;
    const maxRetries = config.retry ?? 3;

    if (config.__retryCount >= maxRetries) {
      return Promise.reject(error);
    }

    config.__retryCount += 1;

    // Calculate delay with exponential backoff (default base: 1000ms)
    const baseDelay = config.retryDelay ?? 1000;
    const delay = baseDelay * Math.pow(2, config.__retryCount - 1);

    console.warn(`[API Retry] Request to ${config.url} failed. Retrying ${config.__retryCount}/${maxRetries} in ${delay}ms...`);

    await new Promise((resolve) => setTimeout(resolve, delay));
    return api(config);
  }
);


// Auth
export const authAPI = {
  login: (email, password, totpCode = null) => api.post('/auth/login', { email, password, totpCode }),
  authTelegram: (initData) => api.post('/auth/telegram', { initData }),
  register: (email, password, displayName, username, referralCode = null) =>
    api.post('/auth/register', { email, password, displayName, username, referralCode }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  verifyCode: (code) => api.post('/auth/verify-code', { code }),
  resendVerificationCode: () => api.post('/auth/resend-code'),
  logout: () => api.post('/auth/logout'),
  logoutAll: () => api.post('/auth/logout-all'),
  deleteAccount: () => api.delete('/auth/account'),
  
  // 2FA Endpoints
  generate2FA: () => api.post('/auth/2fa/generate'),
  verify2FA: (totpCode) => api.post('/auth/2fa/verify', { totpCode }),
  disable2FA: (password) => api.post('/auth/2fa/disable', { password }),
};

// Users
export const usersAPI = {
  getUser: (userId) => api.get(`/users/${userId}`),
  getUserByUsername: (username) => api.get(`/users/by-username/${username}`),
  updateUser: (userId, data) => api.put(`/users/${userId}`, data),
  searchUsers: (q) => api.get('/users/search', { params: { q } }),
  toggleFollow: (userId) => api.post(`/users/${userId}/follow`),
  getSuggested: () => api.get('/users/suggested'),
  getFollowers: (userId) => api.get(`/users/${userId}/followers`),
  getFollowing: (userId) => api.get(`/users/${userId}/following`),
  getAnalytics: (userId) => api.get(`/users/${userId}/analytics`),
  reportUser: (userId, reason, description) =>
    api.post(`/users/${userId}/report`, { reason, description }),
  requestVerification: (data) => api.post('/users/verification/request', data),
  purchaseVerification: () => api.post('/users/verification/purchase'),
  getVerificationStatus: () => api.get('/users/verification/status'),
  createBot: (data) => api.post('/users/bots', data),
  getMyBots: () => api.get('/users/bots/my'),
  getExploreBots: () => api.get('/users/bots/explore'),
};

// Posts
export const postsAPI = {
  getFeed: (skip = 0, limit = 20, tab = 'all') =>
    api.get('/posts', { params: { skip, limit, tab } }),
  getUserPosts: (userId) => api.get(`/posts/user/${userId}`),
  getCreatorComments: () => api.get('/posts/comments/creator'),
  createPost: (content, mediaUrls = null, isPremiumContent = false, isReel = false, title = null, isVideoPlatform = false, category = null, thumbnailUrl = null, audioTrackId = null, audioTrackOffset = 0.0, audioTrackVolume = 1.0, originalVolume = 1.0, isSensitive = false, translations = null, audioTracks = null, subtitles = null, playlistId = null) => 
    api.post('/posts', { content, mediaUrls, isPremiumContent, isReel, title, isVideoPlatform, category, thumbnailUrl, audioTrackId, audioTrackOffset, audioTrackVolume, originalVolume, isSensitive, translations, audioTracks, subtitles, playlistId }),
  getVideos: (category = null, q = null, sortBy = "newest", skip = 0, limit = 20) =>
    api.get('/posts/videos', { params: { category, q, sortBy, skip, limit } }),
  getPost: (postId) => api.get(`/posts/${postId}`),
  updatePost: (postId, data) => api.put(`/posts/${postId}`, data),
  deletePost: (postId) => api.delete(`/posts/${postId}`),
  toggleLike: (postId) => api.post(`/posts/${postId}/like`),
  reactToPost: (postId, emoji) => api.post(`/posts/${postId}/react`, { emoji }),
  sharePost: (postId) => api.post(`/posts/${postId}/share`),
  savePost: (postId) => api.post(`/posts/${postId}/save`),
  incrementViews: (postId) => api.post(`/posts/${postId}/view`),
  getComments: (postId) => api.get(`/posts/${postId}/comments`),
  addComment: (postId, text, parentCommentId = null) =>
    api.post(`/posts/${postId}/comment`, { text, parentCommentId }),
  editComment: (postId, commentId, text) =>
    api.put(`/posts/${postId}/comments/${commentId}`, { text }),
  deleteComment: (postId, commentId) =>
    api.delete(`/posts/${postId}/comments/${commentId}`),
  approveComment: (postId, commentId) =>
    api.post(`/posts/${postId}/comments/${commentId}/approve`),
  likeComment: (postId, commentId) =>
    api.post(`/posts/${postId}/comments/${commentId}/like`),
  reportPost: (postId, reason, description) =>
    api.post(`/posts/${postId}/report`, { reason, description }),
  searchPosts: (q) => api.get('/posts/search', { params: { q } }),
  getActiveLiveStreams: () => api.get('/posts/live-streams'),
  linkYouTubeChannel: (code, redirectUri) => api.post('/posts/youtube/link-channel', { code, redirectUri }),
  unlinkYouTubeChannel: () => api.post('/posts/youtube/unlink-channel'),
  getYouTubeScannedVideos: () => api.get('/posts/youtube/scanned-videos'),
  importYouTubeVideos: (videoIds, isReel, category) => api.post('/posts/youtube/import-videos', { videoIds, isReel, category }),
};

// Conversations & Messages
export const messagesAPI = {
  getConversations: () => api.get('/conversations'),
  createConversation: (participantId, isE2E = false) => api.post('/conversations', { participantId, isE2E }),
  createGroupConversation: (name, participantIds, isE2E = false, isChannel = false, avatar = null) => api.post('/conversations/group', { name, participantIds, isE2E, isChannel, avatar }),
  getConversationInfo: (convId) => api.get(`/conversations/${convId}/info`),
  getMessages: (convId) => api.get(`/conversations/${convId}/messages`),
  sendMessage: (convId, text, mediaUrl = null, mediaType = null, ttl = null, playlistId = null) => api.post(`/conversations/${convId}/messages`, { text, mediaUrl, mediaType, ttl, playlistId }),
  forwardMessage: (sourceConvId, messageId, targetConvId) => api.post('/messages/forward', { sourceConvId, messageId, targetConvId }),
  searchGifs: (q = '') => api.get('/gifs/search', { params: { q } }),
  reactToMessage: (convId, msgId, emoji) => api.post(`/conversations/${convId}/messages/${msgId}/react`, { emoji }),
  sendTyping: (convId) => api.post(`/conversations/${convId}/typing`),
  markRead: (convId) => api.post(`/conversations/${convId}/read`),
  getUnreadCount: () => api.get('/messages/unread-count'),
  addGroupMember: (convId, userId) => api.post(`/conversations/${convId}/members/add`, { userId }),
  removeGroupMember: (convId, userId) => api.post(`/conversations/${convId}/members/remove`, { userId }),
  renameGroup: (convId, name) => api.put(`/conversations/${convId}/rename`, { name }),
  pushSubscribe: (endpoint, keys) => api.post('/push/subscribe', { endpoint, keys }),
  pushUnsubscribe: () => api.delete('/push/unsubscribe'),
  deleteConversation: (convId) => api.delete(`/conversations/${convId}`),
  clearChatHistory: (convId) => api.delete(`/conversations/${convId}/clear`),
  editMessage: (convId, msgId, text) => api.put(`/conversations/${convId}/messages/${msgId}`, { text }),
  deleteMessage: (convId, msgId) => api.delete(`/conversations/${convId}/messages/${msgId}`),
  pinMessage: (convId, msgId) => api.post(`/conversations/${convId}/pin/${msgId}`),
  unpinMessage: (convId) => api.post(`/conversations/${convId}/unpin`),
  getLinkPreview: (url) => api.get('/messages/link-preview', { params: { url } }),
};

// Notifications
export const notificationsAPI = {
  getNotifications: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAllRead: () => api.put('/notifications/read'),
};

// Search
export const searchAPI = {
  globalSearch: (q, type = 'all') => api.get('/search', { params: { q, type } }),
  getTrending: () => api.get('/trending'),
};

// Bookmarks
export const bookmarksAPI = {
  getBookmarks: () => api.get('/bookmarks'),
};

// Liked posts
export const likedPostsAPI = {
  getUserLikedPosts: (userId) => api.get(`/users/${userId}/liked-posts`),
};




// Upload
const isVideoFile = (file) =>
  (file.type && file.type.startsWith('video/')) ||
  /\.(mp4|webm|mov|avi|mkv|wmv|flv|3gp|hevc|m4v|mpg|mpeg|ts|m2ts|qt)$/i.test(file.name || '');

export const uploadAPI = {
  // Backward compatible:
  //   uploadFile(file)                      -> normal upload
  //   uploadFile(file, true)                -> encrypted upload
  //   uploadFile(file, { onProgress, isEncrypted }) -> with real progress
  uploadFile: async (file, opts = {}) => {
    const isEncrypted = opts === true || (opts && opts.isEncrypted === true);
    const onProgress = (opts && typeof opts === 'object' && opts.onProgress) || null;
    const reportProgress = onProgress
      ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0)
      : undefined;

    // YouTube-style direct-to-R2 for videos: browser uploads straight to the cloud,
    // bypassing the VPS. Falls back to the normal server upload on any failure
    // (e.g. R2/CORS not ready), so nothing ever breaks.
    if (!isEncrypted && isVideoFile(file)) {
      try {
        const { data: presign } = await api.post('/upload/presign', {
          filename: file.name,
          contentType: file.type,
          size: file.size,
        });
        await axios.put(presign.uploadUrl, file, {
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          onUploadProgress: reportProgress,
          timeout: 0,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          transformRequest: [(d) => d], // send the raw File, don't let axios touch it
        });
        return { data: { url: presign.url, filename: presign.filename, type: presign.type } };
      } catch (err) {
        console.warn('Direct R2 upload failed, falling back to server upload:', err?.message || err);
        // fall through to the multipart path below
      }
    }

    const formData = new FormData();
    formData.append('file', file);
    if (isEncrypted) formData.append('is_encrypted', 'true');
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: reportProgress,
      timeout: 0,
    });
  },
};

// Stories
// Feed stories use the main backend (routes/stories.py) via the /feed-stories
// path. NOTE: /api/stories is claimed by the Telega chat (messenger-backend) with
// a different schema, so the feed must use its own path to reach the right backend.
export const storiesAPI = {
  getStories: () => api.get('/feed-stories'),
  createStory: (imageUrl, text = '') => api.post('/feed-stories', { imageUrl, text }),
  viewStory: (storyId) => api.post(`/feed-stories/${storyId}/view`),
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getReports: (status = 'pending') => api.get('/admin/reports', { params: { status } }),
  resolveReport: (reportId, action) => api.put(`/admin/reports/${reportId}`, null, { params: { action } }),
  getUsers: (skip = 0, limit = 50) => api.get('/admin/users', { params: { skip, limit } }),
  banUser: (userId, reason, shadowBan = false) => api.post(`/admin/users/${userId}/ban`, { reason, shadowBan }),
  unbanUser: (userId) => api.post(`/admin/users/${userId}/unban`),
  verifyUser: (userId) => api.post(`/admin/users/${userId}/verify`),
  deletePost: (postId) => api.delete(`/admin/posts/${postId}`),
  markPostSensitive: (postId) => api.post(`/admin/posts/${postId}/sensitive`),
  makeAdmin: (userId) => api.post(`/admin/make-admin/${userId}`),
  banIP: (ip, reason) => api.post('/admin/ip-ban', { ip, reason }),
  unbanIP: (ip) => api.delete(`/admin/ip-ban/${ip}`),
  getIPBans: () => api.get('/admin/ip-bans'),
  getMonitoring: () => api.get('/admin/monitoring'),
  getVerificationRequests: (status = 'pending') => api.get('/admin/verification-requests', { params: { status } }),
  approveVerification: (id) => api.post(`/admin/verification-requests/${id}/approve`),
  rejectVerification: (id) => api.post(`/admin/verification-requests/${id}/reject`),
  getWithdrawals: (status = 'pending') => api.get('/admin/withdrawals', { params: { status } }),
  approveWithdrawal: (id) => api.post(`/admin/withdrawals/${id}/approve`),
  rejectWithdrawal: (id, reason = "") => api.post(`/admin/withdrawals/${id}/reject`, { reason }),
  getArtistApplications: (status = 'pending') => api.get('/admin/artist-applications', { params: { status } }),
  approveArtistApplication: (id) => api.post(`/admin/artist-applications/${id}/approve`),
  rejectArtistApplication: (id) => api.post(`/admin/artist-applications/${id}/reject`),
};


// AI Recommendations
export const recommendationsAPI = {
  getRecommendations: () => api.get('/recommendations'),
};

// Video Calls
export const callsAPI = {
  initiateCall: (targetUserId, type = 'video') => api.post('/calls/initiate', { targetUserId, type }),
  answerCall: (callId, action) => api.post(`/calls/${callId}/answer`, { action }),
  signal: (callId, signal) => api.post(`/calls/${callId}/signal`, { signal }),
  endCall: (callId) => api.post(`/calls/${callId}/end`),
};

// E2E Encryption
export const encryptionAPI = {
  storePublicKey: (publicKey) => api.post('/encryption/keys', { publicKey }),
  getPublicKey: (userId) => api.get(`/encryption/keys/${userId}`),
};

export const musicAPI = {
  getTracks: () => api.get('/music'),
  searchTracks: (q) => api.get('/music/search', { params: { q } }),
  uploadTrack: (formData) => api.post('/music/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getSpotifyTracks: () => api.get('/music/spotify/tracks'),
  connectApple: () => api.post('/music/apple/connect'),
  getAppleTracks: () => api.get('/music/apple/tracks'),
  getTrending: () => api.get('/music/trending'),
  getStats: () => api.get('/music/stats'),
  getUserTracks: (userId) => api.get(`/music/user/${userId}`),
  likeTrack: (trackId, track = null) => api.post(`/music/${trackId}/like`, track),
  searchDeezer: (q) => api.get('/music/deezer/search', { params: { q } }),
  searchAudius: (q) => api.get('/music/audius/search', { params: { q } }),
  searchHearThis: (q) => api.get('/music/hearthis/search', { params: { q } }),
  searchRadio: (q) => api.get('/music/radio/search', { params: { q } }),
  getDeezerCharts: () => api.get('/music/deezer/charts'),
  matchYoutubeTrack: (q, duration) => api.get('/music/youtube/match', { params: { q, duration } }),
  createPlaylist: (formData) => api.post('/music/playlists', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getPlaylists: () => api.get('/music/playlists'),
  getPlaylistDetails: (playlistId) => api.get(`/music/playlists/${playlistId}`),
  addTrackToPlaylist: (playlistId, track) => api.post(`/music/playlists/${playlistId}/tracks`, track),
  removeTrackFromPlaylist: (playlistId, trackId) => api.delete(`/music/playlists/${playlistId}/tracks/${trackId}`),
  getArtistDetails: (artistName) => api.get(`/music/artist/details`, { params: { artist: artistName } }),
  getArtistApplicationStatus: () => api.get('/music/artist/application/status'),
  claimArtistProfile: (formData) => api.post('/music/artist/claim', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getArtistDashboard: () => api.get('/music/artist/dashboard'),
  updateArtistProfile: (formData) => api.put('/music/artist/profile', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteTrack: (trackId) => api.delete(`/music/tracks/${trackId}`),
  playTrack: (trackId, track = null) => api.post(`/music/${trackId}/play`, track),
  updateTrackLyrics: (trackId, lyrics) => api.put(`/music/tracks/${trackId}/lyrics`, { lyrics }),
  getAIMix: (prompt) => api.post('/music/ai-mix', { prompt }),
  updatePlaylist: (playlistId, formData) => api.put(`/music/playlists/${playlistId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deletePlaylist: (playlistId) => api.delete(`/music/playlists/${playlistId}`),
  importPlaylist: (source, externalId) => api.post('/music/playlists/import', { source, externalId }),
};


export const paymentsAPI = {
  getConfig: () => api.get('/payments/config'),
  createCheckoutSession: () => api.post('/payments/create-checkout-session'),
  subscribeCreator: (creatorId) => api.post(`/payments/subscribe/${creatorId}`),
  getCryptomusConfig: () => api.get('/payments/cryptomus/config'),
  createCryptomusPremiumSession: () => api.post('/payments/cryptomus/create-premium-session'),
  subscribeCryptomusCreator: (creatorId) => api.post(`/payments/cryptomus/subscribe-creator/${creatorId}`),
  createCryptomusVerificationSession: () => api.post('/payments/cryptomus/create-verification-session'),
};

// Wallet
export const walletAPI = {
    getBalance: () => api.get('/wallet/balance'),
    getRates: () => api.get('/wallet/rates'),
    transfer: (to_user_id, amount, currency, note) => api.post('/wallet/transfer', { to_user_id, amount, currency, note }),
    createTopUpSession: (amount) => api.post('/wallet/topup/checkout', { amount }),
    verifyTopUpSession: (session_id) => api.get(`/wallet/topup/verify?session_id=${session_id}`),
    withdraw: (amount, currency, card_number) => api.post('/wallet/withdraw', { amount, currency, card_number }),
    exchange: (from_currency, to_currency, amount) => api.post('/wallet/exchange', { from_currency, to_currency, amount }),
    getReferrals: () => api.get('/wallet/referrals'),
    depositWeb3: (amount, currency, tx_hash, network) => api.post('/wallet/deposit/web3', { amount, currency, tx_hash, network }),
    simulateTRC20Deposit: (amount) => api.post('/wallet/deposit/trc20/simulate', { amount }),
    claimVfcAirdrop: () => api.post('/wallet/vfc/claim_airdrop'),
    getVfcInfo: () => api.get('/api/vfc/info')
};

// P2P Market
export const p2pAPI = {
    getConfig: () => api.get('/p2p/config'),
    getAds: (params) => api.get('/p2p/ads', { params }),
    createAd: (data) => api.post('/p2p/ads', data),
    deleteAd: (id) => api.delete(`/p2p/ads/${id}`),
    getDeals: () => api.get('/p2p/deals'),
    getDeal: (id) => api.get(`/p2p/deals/${id}`),
    startDeal: (data) => api.post('/p2p/deals', data),
    markPaid: (id) => api.post(`/p2p/deals/${id}/pay`),
    releaseCoins: (id) => api.post(`/p2p/deals/${id}/release`),
    cancelDeal: (id) => api.post(`/p2p/deals/${id}/cancel`),
    getProfile: () => api.get('/p2p/profile'),
    getDealMessages: (id) => api.get(`/p2p/deals/${id}/messages`),
    sendDealMessage: (id, data) => api.post(`/p2p/deals/${id}/messages`, data),
};

export const adsAPI = {
  createCampaign: (data) => api.post('/ads/campaigns', data),
  getCampaigns: () => api.get('/ads/campaigns'),
  getCampaignDetails: (id) => api.get(`/ads/campaigns/${id}`),
  updateCampaign: (id, data) => api.put(`/ads/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/ads/campaigns/${id}`),
  toggleCampaign: (id) => api.post(`/ads/campaigns/${id}/toggle`),
  topupCampaign: (id, amount) => api.post(`/ads/campaigns/${id}/topup`, { amount }),
  serveAds: () => api.get('/ads/serve'),
  trackClick: (campaignId) => api.post('/ads/click', { campaignId }),
  trackImpression: (campaignId) => api.post('/ads/impression', { campaignId }),
  getDemographics: () => api.get('/ads/analytics/demographics'),
  getSavedAudiences: () => api.get('/ads/audiences'),
  saveAudience: (data) => api.post('/ads/audiences', data),
  deleteAudience: (id) => api.delete(`/ads/audiences/${id}`),
};

export const usersUpdateAPI = {
  updateProfile: (userId, data) => api.put(`/users/${userId}`, data),
};

export default api;

